import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Field, Input, InfinityMark, Stack, cx } from '@integra/ui';
import { API_BASE } from '../api/client.js';
import { useAuthStore } from '../auth/store.js';

/**
 * Sign in / register (§14.1).
 *
 * This screen talks to the API directly rather than through the hooks layer,
 * because authentication is what establishes the session the hooks depend on —
 * there is no token yet for `api()` to attach. Every other screen goes through
 * TanStack Query hooks (§21.4).
 */
/** The API's registration rule (§14.1), shown here so a user is never guessing. */
const MIN_PASSWORD = 12;

export function LoginPage() {
  const setSession = useAuthStore((s) => s.setSession);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [organisationName, setOrganisationName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const passwordLongEnough = password.length >= MIN_PASSWORD;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    // Catch the rule the user can see, before a round-trip, and say exactly what
    // to fix rather than the API's generic "Request validation failed".
    if (mode === 'register' && !passwordLongEnough) {
      setError(`Password must be at least ${String(MIN_PASSWORD)} characters.`);
      return;
    }

    setBusy(true);
    try {
      const path = mode === 'login' ? '/auth/login' : '/auth/register';
      const body =
        mode === 'login' ? { email, password } : { email, password, organisationName, displayName };

      const response = await fetch(`${API_BASE}/api/v1${path}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        // Accepts the httpOnly refresh cookie the API sets (§21.4).
        credentials: 'include',
      });

      if (!response.ok) {
        const problem = (await response.json()) as { error?: { message?: string } };
        setError(problem.error?.message ?? 'Sign in failed');
        return;
      }

      const session = (await response.json()) as {
        accessToken: string;
        userId: string;
        organisationId: string;
        platformRole?: 'none' | 'admin';
      };
      setSession({
        accessToken: session.accessToken,
        userId: session.userId,
        organisationId: session.organisationId,
        platformRole: session.platformRole ?? 'none',
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-700 px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <Link
            to="/"
            aria-label="INTEGRA home"
            className="rounded-md transition-opacity duration-fast hover:opacity-80"
          >
            <InfinityMark size={44} className="text-gold-500" />
          </Link>
          <p className="mt-3 font-heading text-3xl tracking-wide text-ink-inverse">INTEGRA</p>
          <p className="mt-1 text-xs uppercase tracking-[0.2em] text-gold-400">
            Multi-Channel Distribution &amp; Activation
          </p>
          <p className="mt-3 text-[11px] uppercase tracking-[0.28em] text-navy-300">
            A Fruitful Bough platform
          </p>
        </div>

        <Card>
          <form onSubmit={(e) => void submit(e)}>
            <Stack gap="md">
              {error !== null && <Alert tone="error">{error}</Alert>}

              {mode === 'register' && (
                <>
                  <Field label="Organisation" htmlFor="org">
                    <Input
                      id="org"
                      value={organisationName}
                      onChange={(e) => setOrganisationName(e.target.value)}
                      placeholder="Fruitful Bough Ltd"
                      required
                    />
                  </Field>
                  <Field label="Your name" htmlFor="name">
                    <Input
                      id="name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      required
                    />
                  </Field>
                </>
              )}

              <Field label="Email" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>

              <Field label="Password" htmlFor="password">
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={mode === 'register' ? MIN_PASSWORD : undefined}
                    required
                    className="pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                    className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-3 text-ink-muted transition-colors duration-fast hover:text-navy-700 focus:outline-none focus-visible:text-navy-700"
                  >
                    {showPassword ? (
                      // eye-off
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <path d="M6.61 6.61A18.5 18.5 0 0 0 2 12s3 8 10 8a9.12 9.12 0 0 0 5.39-1.61" />
                        <line x1="2" y1="2" x2="22" y2="22" />
                      </svg>
                    ) : (
                      // eye
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </Field>

              {mode === 'register' && (
                // Live requirement: the rule, and whether it is met yet, so the
                // user is guided as they type rather than corrected afterwards.
                <div className="-mt-1 flex items-center gap-2 text-xs" aria-live="polite">
                  <span
                    className={cx(
                      'inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold',
                      passwordLongEnough
                        ? 'bg-state-success/15 text-state-success'
                        : 'bg-surface-sunken text-ink-muted',
                    )}
                    aria-hidden="true"
                  >
                    {passwordLongEnough ? '✓' : '•'}
                  </span>
                  <span className={passwordLongEnough ? 'text-state-success' : 'text-ink-muted'}>
                    At least {MIN_PASSWORD} characters
                    {password.length > 0
                      ? ` (${String(Math.min(password.length, MIN_PASSWORD))}/${String(MIN_PASSWORD)})`
                      : ''}
                  </span>
                </div>
              )}

              <Button type="submit" loading={busy}>
                {mode === 'login' ? 'Sign in' : 'Create organisation'}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setMode(mode === 'login' ? 'register' : 'login');
                  setError(null);
                }}
                className="text-xs text-ink-muted underline underline-offset-2 hover:text-navy-700"
              >
                {mode === 'login'
                  ? 'No account? Create an organisation'
                  : 'Already have an account? Sign in'}
              </button>
            </Stack>
          </form>
        </Card>
      </div>
    </div>
  );
}
